import type {
  NotificationCandidateSnapshot,
  NotificationProviderPolicy,
  NotificationResponsibilitySelector,
  NotificationSelectorCohort,
} from '@statura/types';
import { parseNotificationProviderPolicyPatch } from './notification-policy-configuration';

export function notificationSelectorKey(selector: NotificationResponsibilitySelector): string {
  const parsed = parseNotificationProviderPolicyPatch({ primary: selector });
  if (!parsed.ok || !parsed.value.primary) throw new Error('Invalid notification selector');
  return JSON.stringify(parsed.value.primary);
}

/** Same configured cohort expansion for preview and dispatch. Cohorts are facts
 * from the reviewed server adapter, NOT recipient IDs asserted by a browser. */
export function resolveNotificationPolicyCandidates(input: {
  policy: NotificationProviderPolicy;
  cohorts: NotificationSelectorCohort[];
  protectedCandidates: NotificationCandidateSnapshot[];
  phase: 'INITIAL' | 'UNCLAIMED' | 'UNRESOLVED';
}): { candidateSetComplete: boolean; candidates: NotificationCandidateSnapshot[] } {
  const candidates: NotificationCandidateSnapshot[] = [];
  let complete = true;
  const cohorts = new Map<string, NotificationSelectorCohort>();
  try {
    for (const cohort of input.cohorts) {
      const key = notificationSelectorKey(cohort.selector);
      if (cohorts.has(key)) return { candidateSetComplete: false, candidates: [] };
      cohorts.set(key, cohort);
    }
    const stages = input.policy.backups.flatMap((stage, index) =>
      stage.trigger === (input.phase === 'UNRESOLVED' ? 'UNRESOLVED' : 'UNCLAIMED')
        ? [{ selector: stage.selector, tier: index + 1 }]
        : [],
    );
    const tiers = [0, ...stages.map((stage) => stage.tier)];
    const add = (
      selector: NotificationResponsibilitySelector,
      purpose: NotificationCandidateSnapshot['purpose'],
      selectedTiers: number[],
    ) => {
      const cohort = cohorts.get(notificationSelectorKey(selector));
      if (!cohort || !cohort.complete) {
        complete = false;
        return;
      }
      if (
        selector.kind === 'NAMED' &&
        cohort.candidates.some(
          (candidate) =>
            !selector.userIds
              .map((id) => id.toLowerCase())
              .includes(candidate.principalId.toLowerCase()),
        )
      ) {
        complete = false;
        return;
      }
      for (const tier of selectedTiers)
        for (const fact of cohort.candidates)
          candidates.push({ ...fact, tier, purpose, origin: 'PROVIDER' });
    };
    if (input.policy.primary) add(input.policy.primary, 'PRIMARY', [0]);
    for (const stage of stages) add(stage.selector, 'PRIMARY', [stage.tier]);
    for (const selector of input.policy.supporting) add(selector, 'SUPPORTING', tiers);
    for (const selector of input.policy.observers) add(selector, 'OBSERVER', tiers);
    for (const candidate of input.protectedCandidates) {
      if (
        (candidate.origin !== 'PROTECTED' && candidate.origin !== 'PARTICIPANT') ||
        (candidate.origin === 'PARTICIPANT' && candidate.purpose !== 'PARTICIPANT')
      )
        return { candidateSetComplete: false, candidates: [] };
      candidates.push({ ...candidate });
    }
    return { candidateSetComplete: complete, candidates };
  } catch {
    return { candidateSetComplete: false, candidates: [] };
  }
}
